import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../services/api';

function Login() {
  const [loginField, setLoginField] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [darkMode, setDarkMode] = useState(true);
  // Quando a mesma senha abre mais de um acesso da pessoa (CPF em comum), o
  // backend devolve as opções em vez de abrir a sessão, e ela escolhe aqui.
  const [contas, setContas] = useState(null);
  const navigate = useNavigate();
  const { login } = useAuth();
  const usernameRef = useRef(null);
  // Pausa do sistema (Configurações › Avisos): avisa antes de a pessoa tentar
  // entrar. Quem barra é o servidor; o administrador entra mesmo na pausa.
  const [pausa, setPausa] = useState(null);

  useEffect(() => {
    if (usernameRef.current) usernameRef.current.focus();
  }, []);

  useEffect(() => {
    fetch(`${API_BASE_URL}/pausa/`)
      .then((r) => (r.ok ? r.json() : null))
      .then((dados) => {
        const ativa = dados?.ativas?.[0];
        const proxima = dados?.proximas?.[0];
        if (ativa) setPausa({ ativa: true, ...ativa });
        else if (proxima) setPausa({ ativa: false, ...proxima });
      })
      .catch(() => {});
  }, []);

  const quando = (data) => new Date(data).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });

  const performLogin = async (credentials) => {
    setError(null);
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/login/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // É esta requisição que recebe o cookie de sessão; sem `include` o
        // navegador descarta o Set-Cookie e ninguém entra.
        credentials: 'include',
        body: JSON.stringify(credentials),
      });

      const data = await response.json();

      if (response.ok) {
        if (data.needs_account_choice) {
          setContas(data.accounts);
          return;
        }
        login(data.user);
        navigate('/dashboard');
      } else {
        if (data.error) setError(data.error);
        else if (data.non_field_errors) setError(data.non_field_errors[0]);
        else if (data.detail) setError(data.detail);
        else setError('Usuário ou senha incorretos.');
      }
    } catch (err) {
      setError('Erro ao conectar com o servidor.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setContas(null);
    await performLogin({ login: loginField, password });
  };

  // A escolha repete o login pelo nome de usuário, que é sempre único.
  const entrarComoConta = async (username) => {
    setContas(null);
    await performLogin({ login: username, password });
  };

  const currentYear = new Date().getFullYear();

  return (
    <div
      className={`min-h-screen flex items-center justify-center font-['Inter',sans-serif] px-4 ${
        darkMode ? 'text-slate-200' : 'text-slate-700'
      }`}
      style={{
        background: darkMode
          ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)'
          : 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%)',
      }}
    >
      {/* Decorações de fundo */}
      <div
        className={`fixed w-[400px] h-[400px] bg-[#0974BA] rounded-full blur-[80px] -top-24 -right-24 -z-10 ${
          darkMode ? 'opacity-30' : 'opacity-20'
        }`}
      ></div>
      <div
        className={`fixed w-[300px] h-[300px] bg-[#2E4974] rounded-full blur-[80px] -bottom-12 -left-12 -z-10 ${
          darkMode ? 'opacity-30' : 'opacity-20'
        }`}
      ></div>

      {/* Botão de alternar tema */}
      <button
        type="button"
        onClick={() => setDarkMode(!darkMode)}
        aria-label={darkMode ? 'Ativar modo claro' : 'Ativar modo escuro'}
        className={`fixed top-5 right-5 w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 z-10 ${
          darkMode
            ? 'bg-slate-800/80 border border-slate-600/15 text-yellow-400 hover:bg-slate-700/80'
            : 'bg-white/80 border border-slate-200 text-slate-600 hover:bg-slate-100/80 shadow-lg'
        }`}
        title={darkMode ? 'Ativar modo claro' : 'Ativar modo escuro'}
      >
        {darkMode ? (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
          </svg>
        )}
      </button>

      <div className="w-full max-w-[440px]">
        <div
          className={`relative backdrop-blur-xl border rounded-3xl px-8 py-10 sm:px-10 sm:py-12 overflow-hidden ${
            darkMode
              ? 'bg-slate-800/80 border-slate-600/15'
              : 'bg-white/90 border-slate-200 shadow-xl'
          }`}
        >
          {/* Barra gradiente no topo */}
          <div
            className="absolute top-0 left-0 right-0 h-1"
            style={{ background: 'linear-gradient(90deg, #0974BA, #2E4974)' }}
          ></div>

          {/* Logo + Título */}
          <div className="text-center mb-8">
            <div className="mx-auto mb-4 flex items-center justify-center">
              <img src="/LogoNome.svg" alt="SIDEB" className="w-48 h-auto" />
            </div>
            <div
              className={`text-sm mt-1 ${
                darkMode ? 'text-slate-400' : 'text-slate-500'
              }`}
            >
              Sistema de Desempenho Educacional
            </div>
          </div>

          {pausa && !error && (
            <div
              role="status"
              className="mb-5 p-4 border rounded-xl text-sm text-center bg-[#F59E0B]/10 border-[#F59E0B]/40 text-[#B45309]"
              style={darkMode ? { color: '#FBBF24' } : undefined}
            >
              <div className="font-semibold">
                {pausa.ativa
                  ? `Sistema em pausa até ${quando(pausa.fim)}`
                  : `Pausa programada: ${quando(pausa.inicio)} até ${quando(pausa.fim)}`}
                {pausa.perfis?.length > 0 && ' (para alguns perfis)'}
              </div>
              {pausa.mensagem && <div className="mt-1 whitespace-pre-line break-words">{pausa.mensagem}</div>}
              {pausa.ativa && pausa.perfis?.length === 0 && (
                <div className="mt-1 text-xs opacity-80">Só administradores conseguem entrar neste horário.</div>
              )}
            </div>
          )}

          {/* Mensagem de erro */}
          {error && (
            <div
              role="alert"
              className={`mb-5 p-4 border rounded-xl text-sm flex items-start gap-2 ${
                darkMode
                  ? 'bg-[#E83A3C]/10 border-[#E83A3C]/30 text-[#E83A3C]'
                  : 'bg-[#E83A3C]/10 border-[#E83A3C]/20 text-[#E83A3C]'
              }`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 flex-shrink-0 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          {/* Formulário */}
          <form onSubmit={handleLogin}>
            <div className="mb-5">
              <label
                htmlFor="username"
                className={`block text-xs font-medium mb-2 uppercase tracking-wide ${
                  darkMode ? 'text-slate-400' : 'text-slate-500'
                }`}
              >
                Usuário, e-mail ou CPF
              </label>
              <div className="relative">
                <div
                  className={`absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none ${
                    darkMode ? 'text-slate-500' : 'text-slate-400'
                  }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <input
                  ref={usernameRef}
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  value={loginField}
                  onChange={(e) => setLoginField(e.target.value)}
                  required
                  className={`w-full border rounded-xl pl-12 pr-4 py-3.5 text-sm transition-all duration-300 focus:outline-none focus:border-[#0974BA] focus:ring-[3px] focus:ring-[#0974BA]/20 ${
                    darkMode
                      ? 'bg-slate-900/80 border-slate-600/20 text-slate-200 placeholder-slate-600'
                      : 'bg-slate-50 border-slate-200 text-slate-700 placeholder-slate-400'
                  }`}
                  placeholder="Digite seu usuário, e-mail ou CPF"
                />
              </div>
            </div>

            <div className="mb-5">
              <label
                htmlFor="password"
                className={`block text-xs font-medium mb-2 uppercase tracking-wide ${
                  darkMode ? 'text-slate-400' : 'text-slate-500'
                }`}
              >
                Senha
              </label>
              <div className="relative">
                <div
                  className={`absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none ${
                    darkMode ? 'text-slate-500' : 'text-slate-400'
                  }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className={`w-full border rounded-xl pl-12 pr-12 py-3.5 text-sm transition-all duration-300 focus:outline-none focus:border-[#0974BA] focus:ring-[3px] focus:ring-[#0974BA]/20 ${
                    darkMode
                      ? 'bg-slate-900/80 border-slate-600/20 text-slate-200 placeholder-slate-600'
                      : 'bg-slate-50 border-slate-200 text-slate-700 placeholder-slate-400'
                  }`}
                  placeholder="Digite sua senha"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  title={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  className={`absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg transition-colors ${
                    darkMode
                      ? 'text-slate-500 hover:text-slate-300 hover:bg-slate-700/50'
                      : 'text-slate-400 hover:text-slate-600 hover:bg-slate-200/50'
                  }`}
                >
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div className="flex justify-between items-center mb-6">
              <label
                className={`flex items-center gap-2 text-sm cursor-pointer ${
                  darkMode ? 'text-slate-400' : 'text-slate-500'
                }`}
              >
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 accent-[#0974BA] rounded"
                />
                Lembrar de mim
              </label>
              <Link
                to="/forgot-password"
                className="text-[#0974BA] text-sm hover:text-[#F9B047] transition-colors"
              >
                Esqueci a senha
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading || !loginField || !password}
              className="w-full rounded-xl py-4 text-white font-semibold text-base transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_10px_40px_rgba(9,116,186,0.4)] active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none"
              style={{ background: 'linear-gradient(135deg, #0974BA, #2E4974)' }}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Entrando...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  Entrar
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </span>
              )}
            </button>
          </form>

          {/* Footer */}
          <p
            className={`text-center mt-8 text-xs ${
              darkMode ? 'text-slate-600' : 'text-slate-400'
            }`}
          >
            © {currentYear} SIDEB — Secretaria Municipal de Educação
          </p>
        </div>
      </div>

      {/* Escolha do acesso — só aparece quando a senha abre mais de uma conta */}
      {contas && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/60 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="titulo-escolha-acesso"
        >
          <div
            className={`w-full max-w-md rounded-2xl border p-6 shadow-2xl ${
              darkMode
                ? 'bg-slate-800 border-slate-700'
                : 'bg-white border-slate-200'
            }`}
          >
            <h2
              id="titulo-escolha-acesso"
              className={`text-lg font-semibold ${
                darkMode ? 'text-slate-100' : 'text-slate-800'
              }`}
            >
              Com qual acesso deseja entrar?
            </h2>
            <p
              className={`text-sm mt-1 mb-5 ${
                darkMode ? 'text-slate-400' : 'text-slate-500'
              }`}
            >
              Você tem mais de um acesso neste CPF. Cada um tem suas próprias
              permissões e seu próprio histórico.
            </p>

            <div className="space-y-2">
              {contas.map((conta) => (
                <button
                  key={conta.username}
                  type="button"
                  onClick={() => entrarComoConta(conta.username)}
                  disabled={loading}
                  className={`w-full text-left px-4 py-3 rounded-xl border transition-colors disabled:opacity-60 ${
                    darkMode
                      ? 'bg-slate-900/60 border-slate-700 hover:border-[#0974BA] hover:bg-slate-900'
                      : 'bg-slate-50 border-slate-200 hover:border-[#0974BA] hover:bg-white'
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div
                        className={`font-medium truncate ${
                          darkMode ? 'text-slate-100' : 'text-slate-800'
                        }`}
                      >
                        {conta.role_display}
                      </div>
                      <div
                        className={`text-xs truncate ${
                          darkMode ? 'text-slate-500' : 'text-slate-400'
                        }`}
                      >
                        @{conta.username}
                        {conta.city_name ? ` · ${conta.city_name}` : ''}
                      </div>
                    </div>
                    <svg
                      className="w-5 h-5 flex-shrink-0 text-[#0974BA]"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={() => setContas(null)}
              className={`w-full mt-4 py-2.5 text-sm rounded-xl border transition-colors ${
                darkMode
                  ? 'border-slate-700 text-slate-400 hover:bg-slate-700/40'
                  : 'border-slate-200 text-slate-500 hover:bg-slate-50'
              }`}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Login;
